import { useState } from "react"
import { Box } from "./Box"
import Icons from "./Icons"
import { Inline } from "./Inline"
import { usePastedFiles, useFileDrop } from "./react-hooks"
import { Stack } from "./Stack"
import { Text } from "./Text"

function defaultRejectionMessage(file: File): string {
	return `${file.name} cannot be added here.`
}

/**
 * An area that takes files dropped anywhere on it or pasted into it, around
 * content of its own — a reply box, a list. While files are dragged over it, a
 * dashed overlay with `label` says a drop is welcome.
 *
 * Files `accept` does not allow are named in a message above the content,
 * worded by `rejectionMessage`. Without `multiple`, a drop or paste of a file
 * it does not allow, or of more than one file, is refused whole. With it, the
 * allowed files are still taken. `onFiles` may answer with a message of its
 * own, about files it could not take, to show beside those.
 *
 * A drop area or file field nested inside takes what lands on it first.
 */
export function FileDropArea({
	accept,
	multiple = false,
	label,
	isDisabled = false,
	onFiles,
	rejectionMessage = defaultRejectionMessage,
	oneFileMessage = "Add one file.",
	children,
}: {
	accept?: string
	multiple?: boolean
	label: string
	isDisabled?: boolean
	onFiles: (files: Array<File>) => string | void
	rejectionMessage?: (file: File) => string
	/** Why a drop of several files is refused, without `multiple`. */
	oneFileMessage?: string
	children: React.ReactNode
}) {
	const [message, setMessage] = useState<string | null>(null)

	function take(files: FileList, rejected: Array<File>) {
		if (!multiple) {
			if (rejected.length) {
				setMessage(rejectionMessage(rejected[0]))
				return
			}
			if (files.length > 1) {
				setMessage(oneFileMessage)
				return
			}
		}

		const accepted = Array.from(files).filter(
			(file) => !rejected.includes(file)
		)
		const messages = rejected.map(rejectionMessage)
		const answer = accepted.length ? onFiles(accepted) : undefined
		if (answer) messages.push(answer)

		setMessage(messages.length ? messages.join(" ") : null)
	}

	const { isDragging, dropHandlers } = useFileDrop(take, accept, isDisabled)
	const { pasteHandlers } = usePastedFiles(take, accept, isDisabled)

	return (
		<Box position="relative" {...dropHandlers} {...pasteHandlers}>
			<Stack gap="2">
				{message ? (
					<Text color="danger" fontSize="sm" role="alert">
						{message}
					</Text>
				) : null}
				{children}
			</Stack>
			{isDragging ? (
				// Lets the drag events through to the content below, so a file
				// field there takes a drop that lands on it.
				<Inline
					aria-hidden="true"
					position="absolute"
					alignItems="center"
					justifyContent="center"
					rounded="md"
					bgColor="default"
					borderWidth="2"
					borderColor="primary_emphasis"
					pointerEvents="none"
					style={{ inset: 0, borderStyle: "dashed", opacity: 0.95 }}
				>
					<Text fontWeight="semibold">
						<Icons.PaperClip /> {label}
					</Text>
				</Inline>
			) : null}
		</Box>
	)
}
